<template>
    <BaseField
        :label="label"
        :placeholder="placeholder"
        :hint="hint"
        :is-optional="isOptional"
        :name="name"
        :error-messages="errorMessagesTotal"
        data-spec-class="field-check"
        label-wrapper-class="flex items-center flex-row-reverse gap-0"
        class="cursor-pointer"
        :custom-theme-base="customThemeBase"
        @label-click="$emit('update:modelValue', !modelValue)"
    >
        <div class="relative flex items-center rounded-md">
            <input
                v-bind="$attrs"
                ref="input"
                :type="TextFieldType.Checkbox"
                :name="name"
                :class="classObject"
                :placeholder="placeholder"
                :disabled="isDisabled"
                :checked="modelValue"
                :data-spec-class="dataSpecClass"
                @change="$emit('update:modelValue', $event.target.checked)"
                @blur="hasAutoDirty ? vuelidateField?.$touch() : () => {}"
            />
        </div>
    </BaseField>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { FormTheme, TextFieldType, Theme } from '@/types';
import { useField } from '@/composables/field';
import BaseField from './BaseField.vue';

const { focusRingClasses, disabledThemeFieldClasses } = useField();
defineEmits(['update:modelValue']);

const input = ref(null);

function focus() {
    input.value.focus();
}

function select() {
    input.value.select();
}

defineExpose({ focus, select });

interface Props {
    label?: string;
    hint?: string;
    placeholder?: string;
    isOptional?: boolean;
    isDisabled?: boolean;
    modelValue?: boolean;
    vuelidateField?: unknown;
    errorMessages?: string[];
    name: string;
    dataSpecClass?: string;
    hasAutoDirty?: boolean;
    customThemeBase?: FormTheme;
}

const props = withDefaults(defineProps<Props>(), {
    label: '',
    hint: '',
    placeholder: '',
    isOptional: false,
    isDisabled: false,
    modelValue: false,
    vuelidateField: undefined,
    errorMessages: () => [],
    dataSpecClass: 'field-input__check',
    hasAutoDirty: true,
    customThemeBase: undefined,
});

const vuelidateErrors = computed(() => {
    if (props.vuelidateField?.$errors.length) {
        return props.vuelidateField.$errors.map((error) => error.$message);
    }

    return [];
});

const errorMessagesTotal = computed(() => [...vuelidateErrors.value, ...props.errorMessages]);

const theme = computed(() => {
    if (props.isDisabled) {
        return Theme.Disabled;
    }
    if (errorMessagesTotal.value.length) {
        return Theme.Danger;
    }

    return Theme.Normal;
});

const themeBase = computed(() => {
    const defaultThemeBase = 'h-4 w-4 mr-3 rounded-sm';
    return defaultThemeBase;
});

const themeNormal = computed(() => {
    const defaultThemeNormal = `border-tertiary text-brand ${focusRingClasses} focus:ring-brand`;
    return defaultThemeNormal;
});

const themeError = computed(() => {
    return `border-danger text-danger ${focusRingClasses} focus:ring-danger`;
});

const classObject = computed(() => ({
    'transition-ease cursor-pointer': true,
    [themeBase.value]: true,
    [themeNormal.value]: theme.value === Theme.Normal,
    [themeError.value]: theme.value === Theme.Danger,
    [disabledThemeFieldClasses]: theme.value === Theme.Disabled,
}));
</script>

<script lang="ts">
export default {
    inheritAttrs: false,
};
</script>

<style scoped>
:deep([data-spec-class='field__label']) {
    margin-bottom: 0;
}
</style>
